import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ChevronLeft } from "@mui/icons-material";
import { useAuth } from "context/Auth";
import ticketsService from "services/tickets";
import rsvpsService from "services/rsvps";
import { ITicket } from "types/tickets";
import { IRSVP } from "types/rsvps.d";
import LoadingOverlay from "components/LoadingOverlay";
import BasicLayout from "components/Layouts/BasicLayout";
import TicketForm from "components/TicketForm/TicketForm";
import membersService from "services/members";
import { IMember } from "types/members";

const Ticket: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [ticket, setTicket] = useState<Partial<ITicket>>({});
  const [validationResult, setValidationResult] = useState<any>(null);
  const [rsvp, setRsvp] = useState<IRSVP | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [member, setMember] = useState<IMember | null>(null);

  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  // Get eventId and rsvpId from URL parameters
  const eventId = searchParams.get('eventId');
  const rsvpId = searchParams.get('rsvpId');
  const memberId = searchParams.get('memberId');

  useEffect(() => {
    const fetchTicket = async () => {
      try {
        setLoading(true);
        const _ticket = await ticketsService.fetch(id);
        console.log(_ticket);
        setTicket(_ticket);
      } catch (e) {
        console.error("Error fetching ticket:", e);
        setError("Failed to fetch ticket details");
      } finally {
        setLoading(false);
      }
    };

    // Fetch RSVP if rsvpId is provided
    const fetchRsvp = async () => {
      if (!rsvpId) return;

      try {
        setLoading(true);
        const _rsvp = await rsvpsService.fetch(rsvpId);
        setRsvp(_rsvp);
      } catch (e) {
        console.error("Error fetching RSVP:", e);
        setError("Failed to fetch RSVP details");
      } finally {
        setLoading(false);
      }
    };

    const fetchMember = async () => {
      if (!memberId) return;

      try {
        setLoading(true);
        const _member = await membersService.fetch(memberId);
        setMember(_member);
      } catch (e) {
        console.error("Error fetching member:", e);
        setError("Failed to fetch member details");
      } finally {
        setLoading(false);
      }
    };

    if (id && id !== "create") {
      fetchTicket();
    }

    if (rsvpId) {
      fetchRsvp();
    }

    if (memberId) {
      fetchMember();
    }
  }, [id, rsvpId, memberId]);

  // Initialize the ticket with URL parameters for a new ticket
  useEffect(() => {
    if (id === "create") {
      const newTicket: Partial<ITicket> = {};

      if (eventId) {
        newTicket.event = eventId as any;
      }

      if (rsvpId) {
        newTicket.rsvp = rsvpId as any;
      }

      if (memberId) {
        newTicket.member = memberId as any;
      }

      setTicket(newTicket);
    }
  }, [id, eventId, rsvpId, memberId]);

  const handleSave = async () => {
    try {
      setLoading(true);
      setError("");

      if (!ticket.organization || !ticket.event || !ticket.member || !ticket.ticketType) {
        throw new Error("Please fill in all required fields");
      }

      if (id === "create") {
        ticket.createdBy = user;
        ticket.createdDate = new Date().toISOString();
        await ticketsService.create(ticket);
      } else {
        ticket.updatedBy = user;
        ticket.updatedDate = new Date().toISOString();
        await ticketsService.update(id, ticket);
      }
      navigate("/tickets");
    } catch (error: any) {
      console.error("Error saving ticket:", error);
      setError(error.message || "Failed to save ticket");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      setLoading(true);
      await ticketsService.remove(id);
      navigate("/tickets");
    } catch (error) {
      console.error("Error deleting ticket:", error);
      setError("Failed to delete ticket");
    } finally {
      setLoading(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleValidate = async () => {
    if (!id || id === "create") {
      setError("Please save the ticket first before validating");
      return;
    }

    try {
      setLoading(true);
      const result = await ticketsService.validate(id);
      setValidationResult(result);
      setError("");
    } catch (error: any) {
      console.error("Error validating ticket:", error);
      setError(error.message || "Failed to validate ticket");
    } finally {
      setLoading(false);
    }
  };

  return (
    <BasicLayout>
      <div className="w-full">
        <div className="space-y-2">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <button
                onClick={() => navigate(-1)}
                className="mr-4 p-2 rounded-full hover:bg-gray-200 transition-colors"
              >
                <ChevronLeft className="h-6 w-6" />
              </button>
              <h1 className="text-2xl font-bold text-gray-900">
                {id === "create" ? "Create Ticket" : "Edit Ticket"}
              </h1>
            </div>
            <div className="flex flex-row space-x-4">
              {/* <button
                type="button"
                onClick={handleValidate}
                disabled={loading || !id || id === "create"}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50"
              >
                {loading ? 'Validating...' : 'Validate Ticket'}
              </button> */}
              <button
                type="button"
                onClick={handleSave}
                disabled={loading}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              >
                {loading ? 'Saving...' : 'Save Ticket'}
              </button>
            </div>
          </div>

          {/* Error Alert */}
          {error && (
            <div className="rounded-md bg-red-50 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">{error}</h3>
                </div>
              </div>
            </div>
          )}

          {/* Delete Confirmation */}
          {showDeleteConfirm && (
            <div className="rounded-md bg-red-50 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">Are you sure you want to delete this ticket?</h3>
                  <div className="mt-2 flex space-x-3">
                    <button
                      type="button"
                      onClick={handleDelete}
                      className="inline-flex items-center px-2 py-1 border border-transparent text-sm font-medium rounded text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                    >
                      Yes, delete
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowDeleteConfirm(false)}
                      className="inline-flex items-center px-2 py-1 border border-gray-300 text-sm font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Validation Result Alert */}
          {validationResult && (
            <div className="rounded-md bg-blue-50 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2h-1V9a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-blue-800">Validation Result</h3>
                  <div className="mt-2 text-sm text-blue-700">
                    <pre className="whitespace-pre-wrap">
                      {JSON.stringify(validationResult, null, 2)}
                    </pre>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Ticket Form */}
          <div className="bg-white shadow overflow-hidden sm:rounded-lg p-6">
            <TicketForm
              ticket={ticket}
              setTicket={setTicket}
              user={user}
              rsvp={rsvp}
              member={member}
            />
          </div>

          {/* Danger Zone */}
          {id && id !== "create" && (
            <div className="bg-white shadow overflow-hidden sm:rounded-lg p-6 mt-8">
              <h2 className="text-lg font-medium text-red-600 mb-4">Danger Zone</h2>
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(true)}
                disabled={loading}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
              >
                {loading ? 'Deleting...' : 'Delete Ticket'}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Loading Overlay */}
      {loading && <LoadingOverlay loading={loading} />}
    </BasicLayout>
  );
};

export default Ticket; 